<script setup lang="ts">
import { computed } from "vue";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { ChevronDownIcon } from "@heroicons/vue/24/solid";
import { useProductReviewCategoryCollectionStore } from "~/stores/shop/review/useProductReviewCategoryCollectionStore";

const props = defineProps({
    selected: String,
});

const productReviewCategoryCollectionStore = useProductReviewCategoryCollectionStore();

useAsyncData('productCategoryCollectionStore', async () => productReviewCategoryCollectionStore.fetchList());
const items = computed(() => productReviewCategoryCollectionStore.list);
const selectedItem = computed(() => {
    if (productReviewCategoryCollectionStore.list === undefined || !props.selected) {
        return {
            title: 'All reviews',
            text: '',
            slug: '',
            svgInline: '',
            url: '',
        };
    }

    return productReviewCategoryCollectionStore.list.find((item) => item.slug === props.selected);
});
</script>

<template>
    <Menu v-if="items !== undefined && items.length > 0" as="div" class="relative block md:hidden text-left">
        <div>
            <MenuButton
                class="inline-flex w-full justify-center rounded-md border-indigo-500 border-2 px-4 py-2 font-medium text-indigo-800 focus:outline-none"
            >
                {{ selectedItem?.title }}
                <ChevronDownIcon
                    class="ml-2 mt-[0.2rem] h-5 w-5 text-indigo-800 ui-open:rotate-180 transition duration-300"
                    aria-hidden="true"
                />
            </MenuButton>
        </div>

        <transition
            enter-active-class="transition duration-100 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-75 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
        >
            <MenuItems class="absolute z-50 w-full border-indigo-500 border-2 mt-2 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            >
                <div class="px-1 py-1">
                    <MenuItem v-slot="{ active }">
                        <nuxt-link to="/reviews" class="group flex w-full items-center rounded-md px-4 py-2 text-gray-900 font-medium">
                            <span class="w-6 h-6 mr-3"></span>
                            All reviews
                        </nuxt-link>
                    </MenuItem>
                    <MenuItem v-for="item in items" :key="item.slug" v-slot="{ active }">
                        <nuxt-link :to="item.url" class="group flex w-full items-center rounded-md px-4 py-2 text-gray-900 font-medium">
                            <span class="w-6 h-6 mr-3" v-html="item.svgInline"></span>
                            {{ item.title }}
                        </nuxt-link>
                    </MenuItem>
                </div>
            </MenuItems>
        </transition>
    </Menu>
    <div v-else class="block md:hidden animate-pulse w-full inline-flex w-full justify-center rounded-md border-indigo-500 border-2 px-4 py-2 font-medium text-indigo-500 focus:outline-none">
        <span class="animate-pulse block h-6 w-[150px] rounded-md bg-soft-white-200"></span>
    </div>
    <ul v-if="items !== undefined && items.length > 0" class="hidden space-y-2 font-medium md:block">
        <li v-for="item in items" :key="item.slug">
            <nuxt-link :to="item.url"
                       :class="selected === item.slug ? 'underline' : ''"
                       class="flex items-center py-2 text-gray-900 rounded-lg dark:text-white hover:underline underline-offset-4"
            >
                <span class="w-6 h-6" v-html="item.svgInline"></span>
                <span class="ml-3">{{ item.title }}</span>
            </nuxt-link>
        </li>
    </ul>
    <ul v-else class="hidden space-y-2 font-medium md:block">
        <li v-for="index in 10" :key="index">
            <div class="flex items-center py-2 text-gray-900 rounded-lg dark:text-white">
                <span class="w-6 h-6">
                    <div class="animate-pulse rounded-full dark:block md:max-w-none w-6 h-6 bg-soft-white-200"></div>
                </span>
                <span class="ml-3"><span class="animate-pulse inline-block h-3 w-[150px] rounded-md bg-soft-white-200"></span></span>
            </div>
        </li>
    </ul>
</template>